/**
 * oauthLogin — the browser-side "Sign in with Microsoft" flow for the LOCAL NVR
 * login (ADR-040 / ADR-047). It is the React port of the Java desktop viewer's
 * `OauthLoginHelper.java` + `LoginController.startMicrosoftLogin`.
 *
 * The NVR has no HTTPS redirect target, so cloudapi brokers the OAuth flow:
 *   1. GET  /ve/ms/authurl?serial=<S>   (Bearer: NVR serial-JWT)  -> { authUrl, state }
 *   2. open authUrl (login.microsoftonline.com) in a popup; user signs in;
 *      cloudapi's /ve/ms/callback handles Entra's redirect and shows a branded page
 *   3. poll GET /ve/ms/status?serial=<S>&state=<state>  every 2s (~3 min) until
 *      status === "active", which carries a short-lived RS256 vouch
 *   4. hand the vouch to the NVR RPC auth.loginSessionOauth, which verifies it
 *      LOCALLY and mints a real NVR session (see AuthActions.loginMicrosoft).
 *
 * The `?serial=` is ignored server-side (cloudapi derives the serial from the
 * JWT); it is sent only to mirror the dview/EEN call shape. The `state` nonce is
 * single-use and round-tripped opaquely, exactly like dview.
 *
 * WHY a `navigate(url)` callback instead of opening the window here: popup
 * blockers only allow window.open() synchronously inside the click gesture, but
 * we don't know the authUrl until after the /authurl round-trip. So the caller
 * (LoginView) opens a blank popup on click and passes a `navigate` that points it
 * at the authUrl once fetched. It also keeps this module free of DOM globals so
 * the flow is unit-testable with injected deps.
 *
 * Transport is `veCall` (the shared cloudapi `/ve` REST primitive), so the base
 * URL / dev proxy / JWT-header envelope all live in one place.
 *
 * TWO callers, one flow (see `resultMode`):
 *   - APPLIANCE (provider 'ms'): Bearer serial-JWT, active poll returns a `vouch`.
 *   - PORTAL (provider 'portal/ms'): the hosted CloudNVR viewer. No serial-JWT; the
 *     flow is bound to a first-party cookie, so authurl/status ride
 *     `withCredentials`, and the active poll returns a cloud `session` payload
 *     ({ token, refreshToken, ...login fields }), not a vouch. The deployed viewer
 *     is CROSS-ORIGIN to cloudapi; dev runs it same-origin via setupProxy.
 */

import { veCall } from './veCall';

// Poll cadence mirrors OauthLoginHelper.java (POLL_ATTEMPTS=90 * POLL_INTERVAL_MS=2000 = ~3 min).
export const OAUTH_POLL_ATTEMPTS = 90;
export const OAUTH_POLL_INTERVAL_MS = 2000;
// Wall-clock ceiling: attempt-count alone can stretch to ~18 min if each /status
// poll rides its full veCall timeout on a slow/unreachable cloudapi. Cap elapsed
// time so a stalled flow (and the disabled login buttons behind it) recovers in a
// bounded window regardless of per-request latency.
export const OAUTH_MAX_ELAPSED_MS = 200000; // ~3.3 min

// User-facing messages, mirrored from OauthLoginHelper.java so the three viewers read alike.
export const OAUTH_ERR = {
  START_FAILED: 'Could not start Microsoft sign-in.',
  INVALID_URL: 'Invalid Microsoft sign-in URL (must use HTTPS).',
  NO_STATE: 'Could not start Microsoft sign-in (missing state).',
  NO_CREDENTIAL: 'Microsoft sign-in completed but returned no credential.',
  NOT_PERMITTED: 'Microsoft sign-in was not permitted.',
  REJECTED: 'This NVR rejected the Microsoft sign-in.',
  TIMEOUT: 'Microsoft sign-in timed out. Please try again.',
  CANCELLED: 'Microsoft sign-in was cancelled.',
  POPUP_BLOCKED: 'Could not open the Microsoft sign-in window. Please allow pop-ups and try again.',
};

// Statuses that end the flow. cloudapi emits "failed" today; the extra terminal
// words are defensive so a future broker state (expired/denied/revoked/error)
// fails fast instead of the client polling to the timeout budget.
const OAUTH_TERMINAL_STATUSES = ['failed', 'expired', 'denied', 'revoked', 'error'];

/**
 * The authUrl must be an absolute HTTPS URL (rejects http://, javascript:, a
 * relative path, or a non-string). Mirrors OauthLoginHelper.isValidAuthUrl.
 * @param {*} url
 * @returns {boolean}
 */
export const isValidAuthUrl = (url) =>
  typeof url === 'string' && /^https:\/\//i.test(url.trim());

/**
 * Pure classifier for a /ve/ms/status poll response. Mirrors
 * OauthLoginHelper.interpretStatus.
 *   - active + non-empty vouch -> { state:'active', vouch }
 *   - active + empty vouch     -> { state:'failed', reason: NO_CREDENTIAL }
 *   - failed                   -> { state:'failed', reason: reason || NOT_PERMITTED }
 *   - linking / none / unknown -> { state:'pending' }  (keep polling)
 * @param {Object} data - the status response body
 * @returns {{state:'active'|'failed'|'pending', vouch?:string, reason?:string}}
 */
export const interpretStatus = (data, resultMode = 'vouch') => {
  const status = data && data.status;
  if (status === 'active') {
    if (resultMode === 'session') {
      // Portal: the active poll carries the cloud session payload (with `token`),
      // not a vouch. Return the whole body so the caller can ingest it.
      const token = data && data.token;
      if (token && String(token).length) return { state: 'active', session: data };
      return { state: 'failed', reason: OAUTH_ERR.NO_CREDENTIAL };
    }
    const vouch = data && data.vouch;
    if (vouch && String(vouch).length) return { state: 'active', vouch: String(vouch) };
    return { state: 'failed', reason: OAUTH_ERR.NO_CREDENTIAL };
  }
  if (status && OAUTH_TERMINAL_STATUSES.indexOf(status) !== -1) {
    return { state: 'failed', reason: (data && data.reason) || OAUTH_ERR.NOT_PERMITTED };
  }
  return { state: 'pending' };
};

/**
 * A poll error that will not self-heal (the caller should stop, not retry).
 * A 401/403 on /ve/ms/status means the serial-JWT is bad / not enrolled, so
 * retrying to the timeout budget just hides an actionable failure. 5xx, network
 * errors, and timeouts stay transient (a rollout or blip should keep polling).
 * @param {*} err - an axios error
 * @returns {boolean}
 */
export const isHardPollError = (err) => {
  const status = err && err.response && err.response.status;
  return status === 401 || status === 403;
};

/**
 * Best-effort extraction of the `state` nonce from the authUrl query, as a
 * fallback when the /authurl response omits a top-level `state` key. The contract
 * returns `state` explicitly, but the value is also embedded in the authorize URL,
 * so parsing it keeps the poll working if the response shape ever narrows.
 * @param {string} authUrl
 * @returns {string} the state value, or '' if none
 */
export const extractStateFromUrl = (authUrl) => {
  try {
    const q = String(authUrl).split('?')[1] || '';
    for (const pair of q.split('&')) {
      const [k, v] = pair.split('=');
      if (k === 'state') return decodeURIComponent(v || '');
    }
  } catch (e) { /* ignore */ }
  return '';
};

/**
 * Pick the serial-JWT (sApiToken) of the SERVING NVR from the pre-login
 * config.server.getAllServers list. The vouch cloudapi mints is bound to the
 * serial in the token it authenticates, and the NVR that later verifies it
 * (auth.loginSessionOauth on the serving box) rejects any vouch whose serial
 * isn't its own. On a CONTROLLER (eView) the list contains member servers too,
 * and the serving NVR is NOT guaranteed to be index 0 — so we must match by
 * bSerial, never blindly take authServers[0] (which mints a vouch for the wrong
 * appliance and fails with an opaque "Access Denied").
 *
 * Returns '' when the serving serial isn't found (fail-safe: the caller hides
 * the button rather than sending a mismatched token).
 * @param {Array<Object>} authServers - config.server.getAllServers entries
 * @param {number|string} servingSerial - the serving NVR's bSerial (general.bSerial)
 * @returns {string} the serving server's sApiToken, or ''
 */
export const selectServingApiToken = (authServers, servingSerial) => {
  if (!Array.isArray(authServers) || servingSerial == null) return '';
  const match = authServers.find(
    (s) => s && String(s.bSerial) === String(servingSerial)
  );
  return (match && match.sApiToken) || '';
};

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** GET /ve/<provider>/authurl[?serial=<S>]  ->  { authUrl, state } */
export const fetchAuthUrl = async ({ provider, serial, token, withCredentials }) => {
  const params = serial != null ? { serial } : undefined;
  const res = await veCall(`${provider}/authurl`, { params, token, withCredentials });
  return res.data || {};
};

/** GET /ve/<provider>/status?state=<state>[&serial=<S>]  ->  { status, vouch?|token?, reason? } */
export const pollStatus = async ({ provider, serial, state, token, withCredentials }) => {
  const params = serial != null ? { serial, state } : { state };
  const res = await veCall(`${provider}/status`, { params, token, withCredentials });
  return res.data || {};
};

/**
 * Run the full authurl -> browser -> poll -> vouch flow. Resolves with the vouch
 * string, or throws an Error whose `.message` is user-facing and `.code === 'flow'`
 * (so callers can distinguish an OAuth-flow failure from the later NVR-RPC states
 * noauth/maxsession/noremote).
 *
 * @param {Object} args
 * @param {string} [args.provider='ms'] - cloudapi provider path segment
 * @param {number|string} args.serial   - NVR serial (round-tripped; ignored server-side)
 * @param {string} args.token           - NVR serial-JWT (sApiToken), Bearer for /ve/ms/*
 * @param {function(string):void} [args.navigate] - point the pre-opened window at authUrl
 * @param {Object} [args.deps]          - dependency injection for tests
 * @returns {Promise<string>} the vouch
 */
export const runOauthLogin = async ({ provider = 'ms', serial, token, navigate, isCancelled, resultMode = 'vouch', withCredentials = false, deps = {} } = {}) => {
  const {
    fetchAuthUrl: doFetch = fetchAuthUrl,
    pollStatus: doPoll = pollStatus,
    sleep = defaultSleep,
    now = () => Date.now(),
    attempts = OAUTH_POLL_ATTEMPTS,
    intervalMs = OAUTH_POLL_INTERVAL_MS,
    maxElapsedMs = OAUTH_MAX_ELAPSED_MS,
  } = deps;

  const flowError = (message, opts = {}) => {
    const err = new Error(message);
    err.code = 'flow';
    if (opts.cancelled) err.cancelled = true;
    return err;
  };
  // True when the user closed the sign-in popup (guarded — reading a closed
  // cross-origin window can throw). Lets an abandoned flow stop within one poll
  // instead of freezing the login form for the whole budget.
  const cancelled = () => {
    try { return typeof isCancelled === 'function' && !!isCancelled(); }
    catch (e) { return false; }
  };

  let authData;
  try {
    authData = await doFetch({ provider, serial, token, withCredentials });
  } catch (e) {
    throw flowError(OAUTH_ERR.START_FAILED);
  }

  const authUrl = authData && authData.authUrl;
  if (!isValidAuthUrl(authUrl)) {
    throw flowError(OAUTH_ERR.INVALID_URL);
  }
  // Without a state nonce the /status poll can never correlate — fail fast rather
  // than navigate + poll `state=''` to the timeout budget.
  const state = (authData && authData.state) || extractStateFromUrl(authUrl);
  if (!state) {
    throw flowError(OAUTH_ERR.NO_STATE);
  }

  if (typeof navigate === 'function') navigate(authUrl);

  const start = now();
  for (let i = 0; i < attempts; i += 1) {
    // Sleep BEFORE the first poll (matches dview): the callback needs time to land.
    await sleep(intervalMs);

    let data;
    try {
      data = await doPoll({ provider, serial, state, token, withCredentials });
    } catch (e) {
      // A definitive auth failure (bad serial-JWT / NVR not enrolled) won't
      // self-heal — stop instead of retrying to the timeout budget.
      if (isHardPollError(e)) throw flowError(OAUTH_ERR.START_FAILED);
      // Transient (5xx / network / timeout): keep trying, unless the user closed
      // the window or the wall-clock cap hit.
      if (cancelled()) throw flowError(OAUTH_ERR.CANCELLED, { cancelled: true });
      if (now() - start >= maxElapsedMs) throw flowError(OAUTH_ERR.TIMEOUT);
      continue;
    }

    // Interpret BEFORE the cancel check so a success that lands as the cloudapi
    // callback closes the popup still wins the race (active is returned, not cancelled).
    const r = interpretStatus(data, resultMode);
    if (r.state === 'active') return resultMode === 'session' ? r.session : r.vouch;
    if (r.state === 'failed') throw flowError(r.reason);
    if (cancelled()) throw flowError(OAUTH_ERR.CANCELLED, { cancelled: true });
    if (now() - start >= maxElapsedMs) throw flowError(OAUTH_ERR.TIMEOUT);
  }
  throw flowError(OAUTH_ERR.TIMEOUT);
};
